import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as E from "fp-ts/lib/Either";
import type { Ok } from "@ps-bridge/ok";
import { ok } from "@ps-bridge/ok";
import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";
import { useDocumentAssistantRetrievalContextPreviewClient } from "@contexts/DocumentAssistantRetrievalContextPreviewClient.context";
import { useDocumentAssistantSupplyClient } from "@contexts/DocumentAssistantSupplyClient.context";
import {
  type ContextPreviewError,
  type ContextPreviewRequest,
  type ContextPreviewResult,
} from "@services/clients/DocumentAssistantRetrievalContextPreviewApi";
import {
  makeSupplyBatchId,
  makeSupplyByteDigest,
  makeSupplyDisplayName,
  makeSupplyId,
  makeSupplyListingId,
  makeSupplySourceHandle,
  makeSupplySourceId,
  type SupplyAcceptedResponse,
  type SupplyError,
  type SupplyManualUploadRequest,
  type SupplyStatusResponse,
} from "@services/clients/DocumentAssistantSupplyApi";
import { evaluateTaskEither } from "@utils/effects/taskEitherRunner";
import ContextPreviewConsolePanel, {
  type ContextPreviewConsoleForm,
  type ContextPreviewConsolePhase,
  type ContextPreviewSupplyForm,
  type ContextPreviewSupplyPhase,
} from "@view/panels/tools/ContextPreviewConsole.panel";
import { contextPreviewConsoleProgramName } from "@ps-bridge/contextPreviewConsole.program";

const defaultListingId = "018f3ad5-39d4-7bd8-9f79-5bb5f6f34c92";
const defaultWorkspaceId = `org:stub-org:listing:${defaultListingId}:workspace-best-bagels-context`;
const defaultPrompt = "When is the meeting with Sarah, and which context would retrieval use for Best Bagels?";
const defaultSupplyFileName = "listing-supply.pdf";

type SupplyDecodeResult<T> =
  | {
      readonly type: "ok";
      readonly value: T;
    }
  | {
      readonly type: "error";
      readonly message: string;
    };

const slugValue = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "document";

const stableSupplySuffix = (listingId: string, displayName: string): string =>
  `${slugValue(listingId).slice(0, 24)}-${slugValue(displayName).slice(0, 40)}`;

const supplyFormFromListing = (listingId: string): ContextPreviewSupplyForm => ({
  displayName: defaultSupplyFileName,
  sourceHandle: `manual-upload:${listingId}:${defaultSupplyFileName}`,
  statusSupplyId: "",
  selectedDocument: Option.none<File>(),
});

const displayNameFromDocument = (document: File): string =>
  document.name.trim().length > 0 ? document.name.trim() : defaultSupplyFileName;

const documentFingerprint = (document: File): string =>
  [
    document.name,
    document.size.toString(),
    document.type || "application/octet-stream",
    document.lastModified.toString(),
  ].map(slugValue).join("-");

const sourceHandleFromDocument = (listingId: string, document: File): string =>
  `manual-upload:${listingId}:${documentFingerprint(document)}`;

const supplyMessage = (error: SupplyError): string => error.message;

const supplyPhaseFromError = (error: SupplyError): ContextPreviewSupplyPhase => {
  if (error.error === "invalidRequest" || error.error === "conflict") {
    return "blocked";
  }
  return "failed";
};

const decodeSupplyRequest = (
  listingId: string,
  form: ContextPreviewSupplyForm,
): SupplyDecodeResult<SupplyManualUploadRequest> => {
  return Option.fold(
    form.selectedDocument,
    () => ({ type: "error", message: "Select a local document before document supply upload." }),
    (selectedDocument) => decodeSelectedDocumentSupplyRequest(listingId, form, selectedDocument),
  );
};

const decodeSelectedDocumentSupplyRequest = (
  listingId: string,
  form: ContextPreviewSupplyForm,
  selectedDocument: File,
): SupplyDecodeResult<SupplyManualUploadRequest> => {
  const displayName = form.displayName.trim().length > 0
    ? form.displayName.trim()
    : displayNameFromDocument(selectedDocument);
  const documentSuffix = documentFingerprint(selectedDocument);
  const suffix = stableSupplySuffix(listingId, `${displayName}-${documentSuffix}`);
  const supplyId = makeSupplyId(`supply-${suffix}`);
  const batchId = makeSupplyBatchId(`batch-${slugValue(listingId).slice(0, 32)}`);
  const supplyDisplayName = makeSupplyDisplayName(displayName);
  const byteDigest = makeSupplyByteDigest(`sha256:${documentSuffix}`);
  const sourceId = makeSupplySourceId(`source-${suffix}`);
  const sourceHandle = makeSupplySourceHandle(sourceHandleFromDocument(listingId, selectedDocument));
  const supplyListingId = makeSupplyListingId(listingId.trim());
  if (supplyId.type === "error") {
    return { type: "error", message: supplyId.message };
  }
  if (batchId.type === "error") {
    return { type: "error", message: batchId.message };
  }
  if (supplyDisplayName.type === "error") {
    return { type: "error", message: supplyDisplayName.message };
  }
  if (byteDigest.type === "error") {
    return { type: "error", message: byteDigest.message };
  }
  if (sourceId.type === "error") {
    return { type: "error", message: sourceId.message };
  }
  if (sourceHandle.type === "error") {
    return { type: "error", message: sourceHandle.message };
  }
  if (supplyListingId.type === "error") {
    return { type: "error", message: supplyListingId.message };
  }
  return {
    type: "ok",
    value: {
      supplyId: supplyId.value,
      batchId: batchId.value,
      displayName: supplyDisplayName.value,
      byteDigest: byteDigest.value,
      sourceId: sourceId.value,
      sourceHandle: sourceHandle.value,
      listingId: Option.some(supplyListingId.value),
    },
  };
};

const decodeStatusSupplyId = (value: string) => makeSupplyId(value.trim());

const phaseFromError = (error: ContextPreviewError): ContextPreviewConsolePhase => {
  if (error.error === "readDenied" && error.message.toLowerCase().includes("reviewed")) {
    return "missingWorkspace";
  }
  if (error.error === "readDenied") {
    return "readDenied";
  }
  if (error.error === "invalidRequest" || error.error === "responseDecodeError") {
    return "malformed";
  }
  if (error.error === "sendAttemptRefused") {
    return "sendRefused";
  }
  return "error";
};

const ContextPreviewConsolePage: React.FC = () => {
  const { listingId: routeListingId } = useParams<"listingId">();
  const contextPreviewClient = useDocumentAssistantRetrievalContextPreviewClient();
  const supplyClient = useDocumentAssistantSupplyClient();
  const initialListingId = routeListingId ?? "";
  const [form, setForm] = useState<ContextPreviewConsoleForm>({
    listingId: initialListingId,
    workspaceId: defaultWorkspaceId,
    workspaceVersion: "1",
    purpose: "answerQuestion",
    prompt: defaultPrompt,
    sendRequestAttempted: false,
  });
  const [supplyForm, setSupplyForm] = useState<ContextPreviewSupplyForm>(
    supplyFormFromListing(initialListingId || defaultListingId),
  );
  const [phase, setPhase] = useState<ContextPreviewConsolePhase>("idle");
  const [message, setMessage] = useState("Enter preview details.");
  const [result, setResult] = useState<OptionTs<ContextPreviewResult>>(Option.none<ContextPreviewResult>());
  const [supplyPhase, setSupplyPhase] = useState<ContextPreviewSupplyPhase>("idle");
  const [supplyMessageText, setSupplyMessageText] = useState(
    "Add a listing document after route or form listing context is explicit.",
  );
  const [acceptedSupply, setAcceptedSupply] = useState<OptionTs<SupplyAcceptedResponse>>(Option.none<SupplyAcceptedResponse>());
  const [supplyStatus, setSupplyStatus] = useState<OptionTs<SupplyStatusResponse>>(Option.none<SupplyStatusResponse>());

  const updateField = useCallback(
    <K extends keyof ContextPreviewConsoleForm>(field: K, value: ContextPreviewConsoleForm[K]): Ok => {
      setForm((current) => ({
        ...current,
        [field]: value,
      }));
      return ok;
    },
    [],
  );

  const updateSupplyField = useCallback(
    <K extends keyof ContextPreviewSupplyForm>(field: K, value: ContextPreviewSupplyForm[K]): Ok => {
      setSupplyForm((current) => ({
        ...current,
        [field]: value,
        ...(field === "selectedDocument"
          ? Option.fold(
              value as OptionTs<File>,
              () => ({
                sourceHandle: `manual-upload:${form.listingId.trim() || "pending-listing"}:${defaultSupplyFileName}`,
              }),
              (selectedDocument) => ({
                displayName: displayNameFromDocument(selectedDocument),
                sourceHandle: form.listingId.trim().length > 0
                  ? sourceHandleFromDocument(form.listingId.trim(), selectedDocument)
                  : `manual-upload:pending-listing:${documentFingerprint(selectedDocument)}`,
              }),
            )
          : {}),
      }));
      return ok;
    },
    [form.listingId],
  );

  const previewRequest = useMemo<OptionTs<ContextPreviewRequest>>(() => {
    const workspaceVersion = Number.parseInt(form.workspaceVersion, 10);
    if (!Number.isFinite(workspaceVersion) || workspaceVersion < 0) {
      return Option.none<ContextPreviewRequest>();
    }
    return Option.some({
      workspaceId: form.workspaceId.trim(),
      workspaceVersion,
      purpose: form.purpose,
      prompt: form.prompt.trim(),
      sendRequestAttempted: form.sendRequestAttempted,
    });
  }, [form]);

  const onPreview = useCallback(async (): Promise<Ok> => {
    return Option.fold(
      previewRequest,
      async () => {
        setResult(Option.none<ContextPreviewResult>());
        setPhase("malformed");
        setMessage("Preview request is malformed; listing id, reviewed workspace id, workspace version, purpose, and prompt are required.");
        return ok;
      },
      async (request) => {
        if (form.listingId.trim().length === 0 || request.workspaceId.length === 0 || request.prompt.length === 0) {
          setResult(Option.none<ContextPreviewResult>());
          setPhase("malformed");
          setMessage("Preview request is malformed; listing id, reviewed workspace id, workspace version, purpose, and prompt are required.");
          return ok;
        }

        setPhase("loading");
        setMessage("Loading retrieval context preview.");
        const response = await evaluateTaskEither(contextPreviewClient.preview(form.listingId.trim(), request));
        if (E.isLeft(response)) {
          setResult(Option.none<ContextPreviewResult>());
          setPhase(phaseFromError(response.left));
          setMessage(response.left.message);
          return ok;
        }
        setResult(Option.some(response.right));
        setPhase("success");
        setMessage("Retrieval context preview loaded.");
        return ok;
      },
    );
  }, [contextPreviewClient, form.listingId, previewRequest]);

  const onSubmit = useCallback((event: React.FormEvent<HTMLFormElement>): Ok => {
    event.preventDefault();
    onPreview()
      .then(() => ok)
      .catch(() => ok);
    return ok;
  }, [onPreview]);

  const onSupplyUpload = useCallback(async (): Promise<Ok> => {
    const listingId = form.listingId.trim();
    setAcceptedSupply(Option.none<SupplyAcceptedResponse>());
    setSupplyStatus(Option.none<SupplyStatusResponse>());
    if (listingId.length === 0) {
      setSupplyPhase("blocked");
      setSupplyMessageText("Listing context is required before document supply upload.");
      return ok;
    }

    const request = decodeSupplyRequest(listingId, supplyForm);
    if (request.type === "error") {
      setSupplyPhase("blocked");
      setSupplyMessageText(request.message);
      return ok;
    }

    setSupplyPhase("uploading");
    setSupplyMessageText("Uploading listing document supply.");
    const response = await evaluateTaskEither(supplyClient.manualUpload(request.value));
    if (E.isLeft(response)) {
      setSupplyPhase(supplyPhaseFromError(response.left));
      setSupplyMessageText(supplyMessage(response.left));
      return ok;
    }
    setAcceptedSupply(Option.some(response.right));
    setSupplyForm((current) => ({
      ...current,
      statusSupplyId: response.right.supplyId,
    }));
    setSupplyPhase("accepted");
    setSupplyMessageText("Document supply accepted for listing review.");
    return ok;
  }, [form.listingId, supplyClient, supplyForm]);

  const onSupplyStatus = useCallback(async (): Promise<Ok> => {
    const decodedSupplyId = decodeStatusSupplyId(supplyForm.statusSupplyId);
    if (decodedSupplyId.type === "error") {
      setSupplyPhase("blocked");
      setSupplyMessageText("Supply status requires a supply id.");
      return ok;
    }

    setSupplyPhase("polling");
    setSupplyMessageText("Reading document supply status.");
    const response = await evaluateTaskEither(supplyClient.status(decodedSupplyId.value));
    if (E.isLeft(response)) {
      setSupplyPhase(supplyPhaseFromError(response.left));
      setSupplyMessageText(supplyMessage(response.left));
      return ok;
    }
    setSupplyStatus(Option.some(response.right));
    setSupplyPhase("statusLoaded");
    setSupplyMessageText("Document supply status loaded.");
    return ok;
  }, [supplyClient, supplyForm.statusSupplyId]);

  return (
    <ContextPreviewConsolePanel
      programName={contextPreviewConsoleProgramName}
      phase={phase}
      message={message}
      form={form}
      result={result}
      supplyPhase={supplyPhase}
      supplyMessage={supplyMessageText}
      supplyForm={supplyForm}
      acceptedSupply={acceptedSupply}
      supplyStatus={supplyStatus}
      onSubmit={onSubmit}
      onFieldChange={updateField}
      onSupplyFieldChange={updateSupplyField}
      onSupplyUpload={onSupplyUpload}
      onSupplyStatus={onSupplyStatus}
    />
  );
};

export default ContextPreviewConsolePage;
